import { CAPABILITIES } from '@/react/lib/capabilities';

import type { NavConfig } from './types';

// The single source of truth for sidebar structure. Visibility is resolved at
// render time by selectVisibleNav() against the user's capabilities; active
// state is derived from the URL by active.ts.
//
// Scoped to the sales portal: Workspace / Sales / Fulfilment / Catalogues / Admin.
// Preset menus and Price modifiers live under Admin because their nav entries
// are the management (set-up-once) pages — a salesperson pulls them into an
// event from the event editor, not from these standalone pages. Fulfilment
// holds the scheduling surfaces used by the fulfilment roles; it is hidden
// from anyone without one of its capabilities.
export const NAV_CONFIG: NavConfig = [
  {
    heading: 'Workspace',
    items: [
      { kind: 'link', label: 'Dashboard', href: '/', icon: 'dashboard' },
      { kind: 'react-mount', component: 'ActivityCentre', label: 'Activity centre' },
    ],
  },
  {
    heading: 'Sales',
    hideWhenEmpty: true,
    items: [
      // Contacts wears the icon sheet's "User" person glyph (no dedicated Contacts icon).
      { kind: 'link', label: 'Contacts', href: '/contacts', icon: 'contacts', activePrefixes: ['/contacts'], requiredCapability: CAPABILITIES.CONTACTS_READ },
      { kind: 'link', label: 'Organisations', href: '/organisations', icon: 'organisations', activePrefixes: ['/organisations'], requiredCapability: CAPABILITIES.ORGANISATIONS_READ },
      { kind: 'link', label: 'Orders', href: '/orders', icon: 'orders', activePrefixes: ['/orders'], requiredCapability: CAPABILITIES.ORDERS_READ },
      { kind: 'link', label: 'Events', href: '/events', icon: 'events', activePrefixes: ['/events'], requiredCapability: CAPABILITIES.EVENTS_READ },
    ],
  },
  {
    heading: 'Fulfilment',
    hideWhenEmpty: true,
    items: [
      { kind: 'link', label: 'Scheduler', href: '/scheduler', icon: 'scheduler', activePrefixes: ['/scheduler'], requiredCapability: CAPABILITIES.SCHEDULER_READ, requiredFeature: 'scheduler' },
    ],
  },
  {
    // What Grain sells: food products and the bundles built from them. Both are
    // read by every product reader (bundles.read mirrors products.read), so
    // they share a heading rather than Bundles sitting under Admin.
    heading: 'Catalogues',
    hideWhenEmpty: true,
    items: [
      { kind: 'link', label: 'Products', href: '/products', icon: 'product', activePrefixes: ['/products'], requiredCapability: CAPABILITIES.PRODUCTS_READ },
      { kind: 'link', label: 'Bundles', href: '/bundles', icon: 'menus', activePrefixes: ['/bundles'], requiredCapability: CAPABILITIES.BUNDLES_READ, requiredFeature: 'bundles' },
    ],
  },
  {
    // Back-office administration maintained by the sales support team. "Admin"
    // is the nature of the work, not a role gate — every entry is still
    // capability-gated below (a `sales_admin` sees only what their role grants).
    heading: 'Admin',
    hideWhenEmpty: true,
    items: [
      { kind: 'link', label: 'Preset menus', href: '/preset_menus', icon: 'menus', activePrefixes: ['/preset_menus'], requiredCapability: CAPABILITIES.PRESET_MENUS_READ },
      { kind: 'link', label: 'Price modifiers', href: '/price_modifiers', icon: 'priceModifiers', activePrefixes: ['/price_modifiers', '/discounts', '/surcharges'], requiredCapability: CAPABILITIES.PRICE_MODIFIERS_READ },
      {
        kind: 'expandable',
        label: 'Equipment',
        icon: 'equipment',
        activePrefixes: ['/inventories', '/resale_items'],
        hideWhenEmpty: true,
        children: [
          { kind: 'sub-link', label: 'Resale', href: '/resale_items', activePrefixes: ['/resale_items'], requiredCapability: CAPABILITIES.RESALE_ITEMS_READ },
          { kind: 'sub-link', label: 'Inventory', href: '/inventories', activePrefixes: ['/inventories'], requiredCapability: CAPABILITIES.INVENTORIES_READ },
        ],
      },
      {
        kind: 'expandable',
        label: 'Availability',
        icon: 'capacities',
        activePrefixes: ['/capacities', '/closed_hours'],
        hideWhenEmpty: true,
        children: [
          { kind: 'sub-link', label: 'Capacities', href: '/capacities', activePrefixes: ['/capacities'], appendServingDate: true, requiredCapability: CAPABILITIES.CAPACITIES_READ },
          { kind: 'sub-link', label: 'Closed hours', href: '/closed_hours', activePrefixes: ['/closed_hours'], requiredCapability: CAPABILITIES.OPERATING_HOURS_READ },
        ],
      },
      { kind: 'link', label: 'Order sources', href: '/order_sources', icon: 'orderSources', activePrefixes: ['/order_sources'], requiredCapability: CAPABILITIES.ORDER_SOURCES_READ },
      { kind: 'link', label: 'Locations', href: '/locations', icon: 'locations', activePrefixes: ['/locations'], requiredCapability: CAPABILITIES.LOCATIONS_READ },
      { kind: 'link', label: 'Users', href: '/users', icon: 'users', activePrefixes: ['/users'], requiredCapability: CAPABILITIES.USERS_READ },
    ],
  },
];
